import { IBlockDto, IRowSettingsDto } from '../../../clients/services';

export class BlockChecker {
    constructor(private readonly block: IBlockDto) {}

    private isTableLayout(): boolean {
        return this.block.blockLayoutType === 'table';
    }

    private getRowSeatCountOverride(rowNr: number): number | undefined {
        const rowSetting = this.getRowSetting(rowNr);
        const override = rowSetting ? (rowSetting as any).seatsInRow : undefined;
        if (!Number.isInteger(override) || override <= 0) {
            return undefined;
        }
        return override;
    }

    rowExists(rowNr: number): boolean {
        if (rowNr < this.startRowCountAt()) {
            return false;
        }

        if (rowNr > this.endRowCountAt()) {
            return false;
        }

        if (this.isSkipped(rowNr)) {
            return false;
        }

        return true;
    }

    seatExists(row: number, seat: number): boolean {
        if (!this.block) {
            return false;
        }

        if (!this.rowExists(row)) {
            return false;
        }

        if (seat < this.startSeatCountAt(row)) {
            return false;
        }

        return !this.seatIsRemoved(row, seat);
    }

    seatIsRemoved(rowNr: number, seat: number): boolean {
        if (this.isTableLayout() && rowNr !== this.startRowCountAt()) {
            return true;
        }

        const rowSetting = this.getRowSetting(rowNr);
        if (!rowSetting) {
            return false;
        }

        if (rowSetting.removeSeats && rowSetting.removeSeats.filter((s) => s === seat).length > 0) {
            return true;
        }

        if (rowSetting.endSeatNumberAt && rowSetting.endSeatNumberAt > 0) {
            return seat > rowSetting.endSeatNumberAt;
        }

        return false;
    }

    endRowCountAt(): number {
        if (this.isTableLayout()) {
            return this.startRowCountAt();
        }
        return this.startRowCountAt() + this.block.numberOfRows - 1;
    }

    startRowCountAt(): number {
        return this.block.startRowsAt ?? 1;
    }

    getBlockSeatCount(): number {
        if (this.isTableLayout()) {
            const tableSeatCount = this.block.tableSeatCount;
            if (Number.isInteger(tableSeatCount) && (tableSeatCount as number) > 0) {
                return tableSeatCount as number;
            }
            return this.block.seatsInRow ?? 8;
        }

        const rowCounts: number[] = [];
        for (let rowNr = this.startRowCountAt(); rowNr <= this.endRowCountAt(); rowNr++) {
            if (this.isSkipped(rowNr)) {
                continue;
            }
            rowCounts.push(this.getSeatCountForRow(rowNr));
        }

        if (rowCounts.length === 0) {
            return this.block.seatsInRow ?? 1;
        }

        return Math.max(...rowCounts);
    }

    getSeatCountForRow(rowNr: number): number {
        if (this.isTableLayout()) {
            return this.getBlockSeatCount();
        }
        return this.getRowSeatCountOverride(rowNr) ?? this.block.seatsInRow ?? 1;
    }

    startSeatCountAt(rowNr: number): number {
        if (this.isTableLayout()) {
            return this.block.startSeatNumbersAt ?? 1;
        }

        const rowSetting = this.getRowSetting(rowNr);
        if (rowSetting && (rowSetting.startSeatNumbersAt || rowSetting.startSeatNumbersAt === 0)) {
            return rowSetting.startSeatNumbersAt;
        }

        if (this.block.blockWideSeatCount) {
            if (rowNr > (this.block.startRowsAt ?? 1)) {
                return this.endSeatCountAt(rowNr - 1) + 1;
            }
            return this.block.startSeatNumbersAt ?? 1;
        }

        return this.block.startSeatNumbersAt ?? 1;
    }

    endSeatCountAt(rowNr: number): number {
        const rowSeatCount = this.getSeatCountForRow(rowNr);
        const rowSettings = this.block.rowSettings?.find((r) => r.row === rowNr);
        if (rowSettings?.gaps && rowSettings.gaps.length > 0 && this.getRowSeatCountOverride(rowNr) === undefined) {
            const sum = rowSettings.gaps.map((g) => g.shiftNrOfGaps).reduce((x, y) => x + y);
            return this.startSeatCountAt(rowNr) + (rowSeatCount - 1) - sum;
        }
        return this.startSeatCountAt(rowNr) + (rowSeatCount - 1);
    }

    getRowSetting(rowNr: number): IRowSettingsDto | undefined {
        if (this.isTableLayout() && rowNr !== this.startRowCountAt()) {
            return undefined;
        }
        return this.block.rowSettings?.find((r) => r.row === rowNr);
    }

    isSkipped(rowNr: number): boolean {
        if (this.isTableLayout()) {
            return false;
        }
        const rowSetting = this.getRowSetting(rowNr);
        if (rowSetting && rowSetting.skipRow) {
            return true;
        }

        return false;
    }

    hasCorridorAfter(rowNr: number): boolean {
        if (this.isTableLayout()) {
            return false;
        }
        const rowSetting = this.getRowSetting(rowNr);
        if (rowSetting && rowSetting.addCorridorAfter) {
            return true;
        }

        return false;
    }
}
